import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FaMap } from 'react-icons/fa';
import { useQuery } from '@tanstack/react-query';
import { useParams } from 'react-router';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { PROJECT_MODE } from '@utils/constants';
import { getLayerByCustomId } from '@utils/map/helpers';
import { getMaptilerKey } from '@utils/api';
import {
	BASE_MAP_LAYER_ID,
	getBaseMaps,
	DEBOUNCE_DELAY,
	DEFAULT_OPACITY,
	DEFAULT_MAP,
	PREFERRED_MAP,
	Z_INDEX,
	ICON_SIZE,
	SELECTOR_POSITION,
} from './constants';
import {
	SelectorContainer,
	SelectorButton,
	SelectorPanel,
	PanelTitle,
	OptionsList,
	OptionButton,
	OptionIcon,
	Divider,
	OpacityRow,
	OpacityLabel,
	OpacitySlider,
	OpacityValue,
} from './StyledBaseMapComponents';

/**
 * BaseMapSelector - Floating button to select base map layer
 */
const BaseMapSelector = () => {
	const { mapObject, projectMode, singleImageSliderOpen, singleImageFeatures } = useProject();
	const { addToast } = useToast();
	const { project_uuid } = useParams();
	const [isOpen, setIsOpen] = useState(false);
	const [selectedMap, setSelectedMap] = useState(DEFAULT_MAP);
	const [opacity, setOpacity] = useState(DEFAULT_OPACITY);
	const [sliderValue, setSliderValue] = useState(DEFAULT_OPACITY);
	const panelRef = useRef(null);
	const baseMapLayerRef = useRef(null);
	const opacityTimeoutRef = useRef(null);
	const hasUserSelected = useRef(false);

	const [tileLoadFailed, setTileLoadFailed] = useState(false);

	const { data: maptilerKey, isError: maptilerKeyError } = useQuery({
		queryKey: ['maptiler-key', project_uuid],
		queryFn: () => getMaptilerKey(project_uuid),
		enabled: !!project_uuid,
		staleTime: Infinity,
		retry: 1,
	});

	// Notify user if the key fetch itself failed
	useEffect(() => {
		if (maptilerKeyError) {
			addToast({
				id: 'maptiler-key-error',
				title: 'MapTiler maps are unavailable',
				message: 'Could not retrieve the map key. Satellite, Terrain, and Streets maps are disabled.',
				bg: 'warning',
				delay: 5000,
			});
		}
	}, [maptilerKeyError, addToast]);

	const maptilerAvailable = !!maptilerKey && !tileLoadFailed;
	const baseMaps = useMemo(() => getBaseMaps(maptilerKey), [maptilerKey]);

	// Switch to preferred map (satellite) once the key is available
	useEffect(() => {
		if (maptilerAvailable && !hasUserSelected.current) {
			setSelectedMap(PREFERRED_MAP);
		}
	}, [maptilerAvailable]);

	// Fall back to default map if selected map requires a key that isn't available
	useEffect(() => {
		if (!maptilerAvailable && baseMaps[selectedMap]?.requiresKey) {
			setSelectedMap(DEFAULT_MAP);
		}
	}, [maptilerAvailable, selectedMap, baseMaps]);

	// Cleanup timeout on unmount
	useEffect(() => {
		return () => {
			if (opacityTimeoutRef.current) clearTimeout(opacityTimeoutRef.current);
		};
	}, []);

	// Close panel when clicking outside
	useEffect(() => {
		const handleClickOutside = e => {
			if (panelRef.current && !panelRef.current.contains(e.target)) setIsOpen(false);
		};

		if (isOpen) document.addEventListener('mousedown', handleClickOutside);

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

	// Create or update base map layer
	useEffect(() => {
		if (!mapObject) return;

		const existingLayer = getLayerByCustomId(mapObject, BASE_MAP_LAYER_ID);
		if (existingLayer) {
			mapObject.removeLayer(existingLayer);
			baseMapLayerRef.current = null;
		}

		const mapConfig = baseMaps[selectedMap];
		if (!mapConfig?.url) return;

		const source = new XYZ({
			url: mapConfig.url,
			attributions: mapConfig.attribution,
			crossOrigin: 'anonymous',
		});

		const layer = new TileLayer({
			source,
			zIndex: Z_INDEX,
			opacity: opacity,
			properties: { customLayerId: BASE_MAP_LAYER_ID },
		});

		// Disable all MapTiler maps and fall back to default if tiles fail to load (e.g. invalid API key)
		let hasFailed = false;
		const handleTileError = () => {
			if (hasFailed) return;
			hasFailed = true;
			console.warn(`Base map "${selectedMap}" tile failed to load. Disabling MapTiler maps.`);
			setTileLoadFailed(true);
			setSelectedMap(DEFAULT_MAP);
			addToast({
				id: 'maptiler-tile-error',
				title: 'MapTiler maps are currently unavailable',
				message: 'There was a problem loading map tiles. The default map has been selected instead.',
				bg: 'warning',
				delay: 5000,
			});
		};
		source.on('tileloaderror', handleTileError);

		mapObject.addLayer(layer);
		baseMapLayerRef.current = layer;

		return () => {
			source.un('tileloaderror', handleTileError);
			mapObject.removeLayer(layer);
			if (baseMapLayerRef.current === layer) baseMapLayerRef.current = null;
		};
	}, [addToast, mapObject, selectedMap, baseMaps]);

	// Update Opacity
	useEffect(() => {
		if (baseMapLayerRef.current) baseMapLayerRef.current.setOpacity(opacity);
	}, [opacity]);

	const handleMapSelect = useCallback(mapId => {
		hasUserSelected.current = true;
		setSelectedMap(mapId);
	}, []);

	const handleOpacityChange = useCallback(e => {
		const value = parseFloat(e.target.value);
		setSliderValue(value);

		if (opacityTimeoutRef.current) {
			clearTimeout(opacityTimeoutRef.current);
		}

		opacityTimeoutRef.current = setTimeout(() => {
			setOpacity(value);
		}, DEBOUNCE_DELAY);
	}, []);

	const togglePanel = useCallback(() => {
		setIsOpen(prev => !prev);
	}, []);

	const SelectedIcon = baseMaps[selectedMap]?.icon;

	/**
	 * Calculate bottom offset based on project mode and slider state
	 * - Single image projects with open slider AND loaded features: position above the slider
	 * - All other cases: default position
	 * Note: The slider only renders when singleImageFeatures has content
	 */
	const isSliderVisible =
		projectMode === PROJECT_MODE.SINGLE_IMAGE &&
		singleImageSliderOpen &&
		singleImageFeatures?.length > 0;

	const bottomOffset = isSliderVisible ? SELECTOR_POSITION.withSlider : SELECTOR_POSITION.default;

	return (
		<SelectorContainer ref={panelRef} $bottom={bottomOffset}>
			{isOpen && (
				<SelectorPanel>
					<PanelTitle>Base Map</PanelTitle>
					<OptionsList>
						{Object.values(baseMaps).map(map => {
							const Icon = map.icon;
							const isDisabled = map.requiresKey && !maptilerAvailable;
							return (
								<OptionButton
									key={map.id}
									$selected={selectedMap === map.id}
									disabled={isDisabled}
									title={isDisabled ? 'MapTiler maps are currently unavailable' : undefined}
									onClick={() => handleMapSelect(map.id)}>
									<OptionIcon>{Icon && <Icon size={ICON_SIZE.sm} />}</OptionIcon>
									{map.name}
								</OptionButton>
							);
						})}
					</OptionsList>

					{selectedMap !== 'none' && (
						<>
							<Divider />
							<OpacityRow>
								<OpacityLabel>Opacity</OpacityLabel>
								<OpacitySlider
									type="range"
									min="0"
									max="1"
									step="0.01"
									value={sliderValue}
									onChange={handleOpacityChange}
								/>
								<OpacityValue>{Math.round(sliderValue * 100)}%</OpacityValue>
							</OpacityRow>
						</>
					)}
				</SelectorPanel>
			)}

			<SelectorButton onClick={togglePanel} title="Base map">
				{SelectedIcon ? (
					<SelectedIcon size={ICON_SIZE.md} />
				) : (
					<FaMap size={ICON_SIZE.md} />
				)}
			</SelectorButton>
		</SelectorContainer>
	);
};

export default BaseMapSelector;
